import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../services/api';

/**
 * Custom hook for loading cascading filter options (Organization → Campus → Group)
 *
 * Handles loading states, error states, and automatic clearing of child options
 * when parent selections change.
 *
 * @param {Object} params - Current filter values
 * @param {string|null} params.organizationId - Selected organization ID
 * @param {string|null} params.campusId - Selected campus ID
 * @returns {Object} Filter options and loading/error states
 */
export function useFilterOptions({ organizationId, campusId }) {
  const [organizations, setOrganizations] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCampuses, setLoadingCampuses] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [organizationsError, setOrganizationsError] = useState(false);
  const [campusesError, setCampusesError] = useState(false);
  const [groupsError, setGroupsError] = useState(false);

  // Load organizations on mount
  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsError(false);
      try {
        const response = await founderDashboardAPI.getOrganizationsList();
        setOrganizations(response.data.organizations || []);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setOrganizations([]);
        setOrganizationsError(true);
      } finally {
        setLoading(false);
      }
    };
    loadOrganizations();
  }, []);

  // Load campuses when organization changes
  useEffect(() => {
    if (!organizationId) {
      setCampuses([]);
      setGroups([]);
      setCampusesError(false);
      setGroupsError(false);
      return;
    }

    const loadCampuses = async () => {
      setLoadingCampuses(true);
      setCampusesError(false);
      try {
        const response = await founderDashboardAPI.getCampuses(organizationId);
        setCampuses(response.data.campuses || []);
      } catch (err) {
        console.error('Failed to load campuses:', err);
        setCampuses([]);
        setCampusesError(true);
      } finally {
        setLoadingCampuses(false);
      }
    };
    loadCampuses();
  }, [organizationId]);

  // Load groups when campus changes
  useEffect(() => {
    if (!campusId) {
      setGroups([]);
      setGroupsError(false);
      return;
    }

    const loadGroups = async () => {
      setLoadingGroups(true);
      setGroupsError(false);
      try {
        const response = await founderDashboardAPI.getGroupsByCampus(campusId);
        setGroups(response.data.groups || []);
      } catch (err) {
        console.error('Failed to load groups:', err);
        setGroups([]);
        setGroupsError(true);
      } finally {
        setLoadingGroups(false);
      }
    };
    loadGroups();
  }, [campusId]);

  return {
    organizations,
    campuses,
    groups,
    loading,
    loadingCampuses,
    loadingGroups,
    organizationsError,
    campusesError,
    groupsError
  };
}
